// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { ActivityListItem } from 'presentations/api/schema/activity';
import { ActivityType } from 'domains/models/activity/activityType';
import {
  formatDateTimeRange,
  formatHostName,
} from 'presentations/views/helpers/formatter';
import { currentTimeZone, formatToDateTime } from 'infras/datetime';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import Avatar from 'presentations/views/components/atoms/Avatar';
import {
  black,
  blueGrayPale,
  gray01,
  gray02,
  gray06,
  primary,
} from 'presentations/views/styles/color';
import EventRoleTag from 'presentations/views/components/molecules/EventRoleTag';
import { EventDetailItem } from 'presentations/api/schema/event';
import { HostOperationType } from 'domains/models/event_type/hostOperationType';
import { useMemo } from 'react';

export interface Props {
  event: EventDetailItem;
  activity: ActivityListItem;
  isGuest?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const guestName = props.event.contact?.name || null;
  const guestProfileImage = props.event.contact?.profileImage || null;

  const iconName = useMemo(() => {
    switch (props.activity.activityType) {
      case ActivityType.EventRescheduled:
        return 'update';
      case ActivityType.EventCanceled:
        return 'close';
    }
    return 'calendar-month';
  }, [props.activity.activityType]);

  const renderContent = (activity: ActivityListItem) => {
    const about = activity.about;
    const user = (props.activity.users && props.activity.users[0]) || null;

    let hostName = null;
    let hostProfileImage = null;
    if (user) {
      hostName = props.isGuest
        ? formatHostName(
            props.event.hostOperationType,
            user.name,
            props.event.team?.name || null,
          )
        : user.name;

      hostProfileImage =
        props.isGuest &&
        props.event.hostOperationType === HostOperationType.Team
          ? props.event.team?.profileImage || null
          : user?.profileImage || null;
    }

    let actionContent = null;
    let isHost = null;

    let name = guestName;
    let profileImage = guestProfileImage;

    switch (activity.activityType) {
      case ActivityType.EventCreated: {
        isHost = false;
        break;
      }
      case ActivityType.EventRescheduled: {
        isHost = !about.by.isGuest;
        if (isHost) {
          name = hostName;
          profileImage = hostProfileImage;
        }
        actionContent = (
          <RescheduleContent>
            <RescheduleDate>
              {formatDateTimeRange(
                about.from.startedAt,
                about.from.endedAt,
                about.from.timeZone,
              )}{' '}
              <RescheduleTimeZone>{about.from.timeZone}</RescheduleTimeZone>
            </RescheduleDate>
            <DestIcon name="arrow-down-thin" />
            <RescheduleDate>
              {formatDateTimeRange(
                about.to.startedAt,
                about.to.endedAt,
                about.from.timeZone,
              )}
              <RescheduleTimeZone>{about.from.timeZone}</RescheduleTimeZone>
            </RescheduleDate>
          </RescheduleContent>
        );
        break;
      }
      case ActivityType.EventCanceled: {
        isHost = !about.by.isGuest;
        if (isHost) {
          name = hostName;
          profileImage = hostProfileImage;
        }
        break;
      }
    }
    return (
      <Content>
        <HostAvatarContainer>
          <HostAvatar src={profileImage}>{name && name.charAt(0)}</HostAvatar>
          <UserName>{name}</UserName>
          {isHost !== null && <RoleTag isHost={isHost} />}
        </HostAvatarContainer>
        {actionContent && <Action>{actionContent}</Action>}
      </Content>
    );
  };
  const title = t(`Activity ${props.activity.activityType}`);
  return (
    <ListItem>
      <Separator>
        <Dot>
          <Icon name={iconName} />
        </Dot>
        <Connector />
      </Separator>
      <MainContent>
        <ActivityContent>
          <Date>
            {formatToDateTime(props.activity.createdAt, currentTimeZone())}
          </Date>
          <Title>{title}</Title>
          {renderContent(props.activity)}
        </ActivityContent>
      </MainContent>
    </ListItem>
  );
}

const ActivityContent = styled.div``;
const Date = styled.div`
  font-size: ${fontSize(13)};
  color: ${gray02};
`;
const Content = styled.div`
  margin-top: ${spacing(8)};
`;
const Action = styled.div`
  margin-top: ${spacing(16)};
`;
const HostAvatarContainer = styled.div`
  margin-right: ${spacing(8)};
  display: flex;
  align-items: center;
`;
const HostAvatar = styled(Avatar)`
  width: 28px;
  height: 28px;
  margin-right: ${spacing(8)};
`;
const Title = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
`;
const UserName = styled.span`
  font-weight: ${fontWeight('bold')};
  color: ${black};
  margin-right: ${spacing(16)};

  ${mediaMax.tablet} {
    margin-right: ${spacing(8)};
  }
`;
const ListItem = styled.div`
  display: flex;
`;
const Separator = styled.div`
  position: relative;
  /* display: flex;
  flex-direction: column;
  align-items: center; */
  width: 48px;
`;
const Connector = styled.div`
  position: absolute;
  top: 0;
  left: 23px;
  width: 2px;
  height: 100%;
  background-color: ${blueGrayPale};
  z-index: 0;
`;
const Dot = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 48px;
  height: 48px;
  background-color: ${gray06};
  color: ${primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  z-index: 1;
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: ${spacing(16)};
  margin-bottom: ${spacing(24)};
`;
const RoleTag = styled(EventRoleTag)`
  font-size: ${fontSize(13)};
`;
const DestIcon = styled(Icon)`
  color: ${gray01};
  width: 24px !important;
  height: 24px !important;
  transform: rotate(270deg);
  margin: 0 ${spacing(8)};
  ${mediaMax.tablet} {
    transform: rotate(0deg);
    margin: ${spacing(8)} auto;
  }
`;
const RescheduleContent = styled.div`
  display: flex;
  align-items: center;
`;
const RescheduleDate = styled.div`
  background-color: ${gray06};
  border-radius: ${borderRadius(6)};
  padding: ${spacing(12)};
`;
const RescheduleTimeZone = styled.span`
  margin-left: ${spacing(8)};
`;
